import { useEffect, useState, type ChangeEvent } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StageLayout } from '@/components/ui/StageLayout';
import { StageActionBar } from '@/components/ui/StageActionBar';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { ApiError } from '@/lib/api/client';
import VoiceFormFiller from '../../components/VoiceFormFiller';
import VoiceMicButton from '../../components/VoiceMicButton';
import { useCreatePatient } from '../patients/useCreatePatient';
import { recordConsents } from '../consent/api';
import { useActiveConsentForms } from '../consent/useConsent';
import { ConsentCapturePanel, type ConsentAnswerValue } from '../consent/ConsentCapturePanel';
import type { ConsentTypeId } from '../consent/types';
import type { NewPatientInput } from '../patients/api';
import type { Patient } from '../patients/types';
import { DEFAULT_TITLE_OPTIONS, useIntakeConfig } from './useIntakeConfig';
import { capitalizeNameParts, nameInputProps, withTitle } from '@/lib/patientName';
import { useIntakeDraft } from './useIntakeDraft';
import type { RegisterFormData } from './types';

/**
 * Drop-in replacement for src/components/stages/RegisterStage.jsx — see
 * VerifyStage.tsx alongside it for the fuller explanation of scope (only
 * these two child stages are migrated; the surrounding VisitWorkflow.jsx
 * stepper container stays legacy).
 */

// Loose on purpose: VisitWorkflow.jsx (the container this mounts inside of)
// is untyped legacy JS, so `visit` arrives as whatever shape the backend
// last returned. This types only the one path this component actually reads.
interface VisitLike {
  stages?: Array<{ stage: string; stage_data?: Record<string, unknown> | null }>;
}

interface RegisterStageProps {
  visit: VisitLike | null;
  completeStage: (stage: string, stageData?: Record<string, unknown>, notes?: string) => void | Promise<void>;
  loading: boolean;
  setPatient: (patient: Patient) => void;
}

const GRID_FULL_WIDTH_FIELDS = new Set(['address', 'abha_number']);

// Names arriving by any route other than typing (voice, the Verify cascade,
// voice intake) get the same first-letter capitals as a typed one — see
// capitalizeNameParts.
function capitalizeName<T extends Record<string, unknown>>(data: T): T {
  return typeof data.name === 'string' ? { ...data, name: capitalizeNameParts(data.name) } : data;
}

// Same pure helper as VerifyStage.tsx/VitalsStage.jsx — DOB is the more
// reliable source of truth once known, so age follows it automatically
// rather than needing separate entry. Duplicated rather than shared since
// there's no common utils module these three already import from.
function ageFromDob(dob: string): string | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? String(age) : null;
}

// intake_form_config.options is NULL for every one of these rows — none of
// these choices come from the database, they are fixed clinical/demographic
// sets. The legacy renderer this replaces only ever special-cased 'gender',
// so any other field_type='select' row silently fell through to a plain
// <input type="select">, which the browser renders as a broken text box —
// see migration_hide_register_blood_group.sql, which worked around that for
// blood_group specifically by hiding it from Register entirely rather than
// fixing the renderer. Now that the renderer handles any known select field,
// that migration's UPDATE was reversed (migration_unhide_register_blood_group.sql)
// and blood_group collects here, cascading to Vitals via the pre-fill it
// already had (VitalsStage.jsx: bloodGroup: p.bloodGroup || patient.blood_group).
//
// marital_status is also field_type='select' in the seed data (status
// 'hidden' by default) — same broken-input-if-toggled-on exposure, fixed
// here in the same pass since it is the identical bug, not separate scope.
// It stays 'hidden' by default; this only keeps it from breaking the moment
// an Admin turns it on via the Intake Form Configurator, same as every other
// hidden field.
const SELECT_FIELD_OPTIONS: Record<string, readonly string[]> = {
  gender: ['Male', 'Female', 'Other'],
  blood_group: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'],
  marital_status: ['Single', 'Married', 'Divorced', 'Widowed'],
};

export default function RegisterStage({ visit, completeStage, loading, setPatient }: RegisterStageProps) {
  const { data: fields, isLoading: fieldsLoading } = useIntakeConfig();
  const [form, setForm] = useState<RegisterFormData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const createPatient = useCreatePatient();

  // ── Registration is two steps: Patient details, then Consent ────────
  //
  // The patients row is written when Complete Registration is pressed, NOT
  // when the details step is left. That ordering is the point: Treatment
  // Consent is mandatory, so a patient who declines it must leave no
  // half-registered record behind. It also means the patient created here is
  // remembered in `createdPatient` — if recording consent or advancing the
  // stage then fails, pressing the button again must not create a second
  // patient with a second UHID.
  const [step, setStep] = useState<'details' | 'consent'>('details');
  const [createdPatient, setCreatedPatient] = useState<Patient | null>(null);
  const { data: consentForms, isLoading: consentLoading, error: consentLoadError } = useActiveConsentForms(true);
  const [consentAnswers, setConsentAnswers] = useState<Partial<Record<ConsentTypeId, ConsentAnswerValue>>>({});
  const [consentNotes, setConsentNotes] = useState<Partial<Record<ConsentTypeId, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const consentItems = consentForms?.items || [];
  const unanswered = consentItems.filter((item) => !consentAnswers[item.consent_type]);
  // The mandatory rule, evaluated exactly as backend/utils/patientConsent.js's
  // hasRequiredConsents evaluates it — the PATCH that completes the Register
  // stage enforces the same thing server-side, so this only decides whether the
  // button is worth pressing.
  const blockedByConsent = consentItems.filter((item) => item.required && consentAnswers[item.consent_type] !== 'accept');
  const consentComplete = unanswered.length === 0 && blockedByConsent.length === 0;
  // A hospital can disable every consent form (Admin Panel → System → Consent
  // Forms). There is then nothing to ask, so Registration is one step again
  // rather than a second screen that says "no questions" — the step only
  // exists to ask something.
  const consentStepNeeded = consentLoading || !!consentLoadError || consentItems.length > 0;

  // See useIntakeDraft's own header comment — autosaves whatever's been
  // typed here (on top of whatever Verify already cascaded in) so a browser
  // closed mid-Register isn't a total loss.
  useIntakeDraft(form);

  const f = (k: string, v: string) => setForm((p) => {
    const next = { ...p, [k]: k === 'name' ? capitalizeNameParts(v) : v };
    // DOB drives age here too, not just on the Verify stage it may have
    // cascaded from — editing DOB directly in Registration (correcting a
    // typo, or filling it in when Verify left it blank) should keep age in
    // step the same way, rather than only computing it once at cascade time.
    if (k === 'dob') {
      const computed = ageFromDob(v);
      if (computed !== null) next.age = computed;
    }
    return next;
  });

  // ── Title (Mr./Mrs./Ms./Miss) ───────────────────────────────────────
  // An intake_form_config row like any other (so mandatory/optional/hidden
  // work unchanged), but drawn attached to the front of the name box —
  // [Mr. ▼][Ramesh Kumar] — rather than as its own grid cell. Its choices are
  // admin data (Admin → Clinical → Intake Form). It is never set from gender,
  // and never by voice: staff choose it.
  const titleField = (fields || []).find((field) => field.field_name === 'title');
  const hasNameField = (fields || []).some((field) => field.field_name === 'name');
  const titleOptions = (() => {
    const configured = (titleField?.options || []).filter((o): o is string => typeof o === 'string' && o.trim() !== '');
    const list = configured.length ? configured : [...DEFAULT_TITLE_OPTIONS];
    // A value carried in from a saved draft that the admin has since removed
    // stays selectable rather than silently blanking.
    return form.title && !list.includes(form.title) ? [form.title, ...list] : list;
  })();
  const gridFields = (fields || []).filter((field) => !(field.field_name === 'title' && hasNameField));

  const titleSelect = titleField ? (
    <NativeSelect
      aria-label={titleField.display_label}
      className="w-[96px] shrink-0"
      value={form.title || ''}
      onChange={(e) => f('title', e.target.value)}
      invalid={!!errors.title}
    >
      <option value="">{titleField.status === 'mandatory' ? 'Title' : '—'}</option>
      {titleOptions.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </NativeSelect>
  ) : null;

  const voiceFields = (fields || []).filter((field) => field.field_name !== 'title').map((field) => ({
    key: field.field_name,
    label: field.display_label,
    type: SELECT_FIELD_OPTIONS[field.field_name] ? 'select' : field.field_type === 'number' ? 'number' : 'text',
    options: SELECT_FIELD_OPTIONS[field.field_name],
  }));
  const applyVoiceFields = (extracted: Record<string, string>) => {
    const cleaned = Object.fromEntries(Object.entries(extracted).filter(([, v]) => v !== '' && v != null));
    setForm((p) => ({ ...p, ...capitalizeName(cleaned) }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    (fields || [])
      .filter((field) => field.status === 'mandatory')
      .forEach((field) => {
        if (!form[field.field_name]?.trim?.()) errs[field.field_name] = 'Required';
      });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinueToConsent = () => {
    if (!validate()) return;
    setErrors({});
    setStep('consent');
  };

  /**
   * Complete Registration: create the patient, record every consent answer in
   * one transaction, then advance the stage. In that order, and only once
   * Treatment Consent has been accepted — the consent rows need a patient_id,
   * so the record has to exist before they can be written, but nothing is
   * written at all while a mandatory consent stands declined.
   */
  const handleCompleteRegistration = async () => {
    if (!validate()) { setStep('details'); return; }
    if (!consentComplete) return;
    setErrors({});
    setSubmitting(true);
    try {
      // Same first-letter capitals on save as while typing, so a name that
      // reached the form some other way is stored formatted too.
      const toSave = capitalizeName(form);
      // `form` is keyed by whatever intake_form_config currently lists, which
      // NewPatientInput now covers in full (see its own comment) — the cast is
      // only because RegisterFormData is an untyped Record<string,string> and
      // TypeScript cannot see that validate() above already guarantees
      // name/mobile/gender are present.
      const patient = createdPatient
        ?? await createPatient.mutateAsync(toSave as unknown as NewPatientInput);
      setCreatedPatient(patient);
      setPatient(patient);

      // Skipped entirely when a hospital has disabled every consent form —
      // there is nothing to record, and the batch endpoint rejects an empty
      // list rather than silently accepting a no-op write.
      if (consentItems.length > 0) {
        await recordConsents(
          patient.uhid,
          consentItems.map((item) => ({
            consent_type: item.consent_type,
            granted: consentAnswers[item.consent_type] === 'accept',
            notes: consentNotes[item.consent_type]?.trim() || null,
          })),
          // No visit row exists yet — this stage is still the PENDING,
          // local-only visit (see VisitWorkflow.tsx), and the real one is
          // created by completeStage below. The consent belongs to the
          // patient, not the visit, so leaving this null loses nothing.
          null,
        );
      }

      await completeStage('register', { ...toSave, uhid: patient.uhid, patient_id: patient.id });
    } catch (e) {
      setErrors({ _: e instanceof ApiError ? e.message : 'Failed to complete registration' });
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-fill from the identity fields already entered (and duplicate-checked)
  // during the mandatory Verify stage — no need to re-type them here.
  useEffect(() => {
    const verifyData = visit?.stages?.find((s) => s.stage === 'verify')?.stage_data;
    if (verifyData) {
      const { name, mobile, dob, gender, age } = verifyData as Record<string, unknown>;
      // Belt-and-braces: Verify's own stage_data should already carry a
      // computed age alongside dob, but fall back to computing it here too
      // in case it doesn't (e.g. a stage_data row written before that
      // computation existed) rather than silently leaving age blank when
      // dob is right there.
      const cascadedAge = age ? String(age) : (dob ? ageFromDob(String(dob)) : null);
      setForm((p) => ({
        ...p,
        ...(name ? { name: capitalizeNameParts(String(name)) } : {}),
        ...(mobile ? { mobile: String(mobile) } : {}),
        ...(dob ? { dob: String(dob) } : {}),
        ...(gender ? { gender: String(gender) } : {}),
        ...(cascadedAge ? { age: cascadedAge } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from voice intake if available (richer fields — address, email —
  // that Verify does not collect). Consumed once, then cleared — see global.d.ts.
  // __voiceIntakeData always carries every key (VoiceIntake.tsx sets '' for
  // anything voice didn't capture), so blanks are filtered out here before
  // merging — otherwise a field voice missed (commonly dob/age) but the
  // Verify-stage cascade above already filled in would get wiped back to
  // empty, even though name/mobile/gender (voice did capture those) looked
  // fine, which is exactly what made this bug easy to miss.
  useEffect(() => {
    const voiceData = window.__voiceIntakeData;
    if (voiceData) {
      const cleaned = Object.fromEntries(Object.entries(voiceData).filter(([, v]) => v !== '' && v != null));
      setForm((p) => ({ ...p, ...capitalizeName(cleaned) }));
      window.__voiceIntakeData = null;
    }
  }, []);

  const missingRequired = (fields || [])
    .filter((field) => field.status === 'mandatory')
    .filter((field) => !form[field.field_name]?.trim?.()).length;

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    {/* Registration → Patient details → Consent → Complete. A sub-stepper
        inside the Register stage rather than a twelfth pipeline stage: consent
        is asked once per PATIENT, and the pipeline's stages are per VISIT. */}
    <div className="flex items-center gap-2 text-[12.5px]">
      <StepChip index={1} label="Patient details" state={step === 'details' ? 'current' : 'done'} />
      <span className="text-muted-foreground">→</span>
      {consentStepNeeded && (
        <>
          <StepChip index={2} label="Consent" state={step === 'consent' ? 'current' : 'todo'} />
          <span className="text-muted-foreground">→</span>
        </>
      )}
      <StepChip index={consentStepNeeded ? 3 : 2} label="Complete registration" state="todo" />
    </div>

    {step === 'details' ? (
    <Card>
      <div className="mb-1.5 text-xl font-bold">📝 Patient registration</div>
      <div className="mb-5 text-[13px] text-ink-muted">
        Fields shown are configured by your admin. Starred fields are mandatory.
      </div>
      {errors._ && <div className="mb-3 text-[13px] text-danger">{errors._}</div>}

      <VoiceFormFiller fields={voiceFields} onFields={applyVoiceFields} label="🎙 Fill this form by voice" />

      {fieldsLoading ? (
        <div className="mb-3.5 text-[13px] text-ink-muted">Loading form…</div>
      ) : (
        <div className="grid2 mb-3.5">
          {gridFields.map((field) => (
            <FormField
              key={field.field_name}
              label={field.display_label}
              required={field.status === 'mandatory' || (field.field_name === 'name' && titleField?.status === 'mandatory')}
              error={field.field_name === 'name' ? (errors.name || (errors.title && 'Select a title')) : errors[field.field_name]}
              className={GRID_FULL_WIDTH_FIELDS.has(field.field_name) ? 'col-span-2' : undefined}
              labelExtra={
                field.field_type === 'text' ? (
                  <VoiceMicButton
                    title={`Dictate ${field.display_label}`}
                    onText={(t: string) => f(field.field_name, (form[field.field_name] ? form[field.field_name] + ' ' : '') + t)}
                  />
                ) : undefined
              }
            >
              {field.field_name === 'name' && titleSelect ? (
                <div className="flex gap-2">
                  {titleSelect}
                  <Input
                    className="min-w-0 flex-1"
                    type={field.field_type}
                    value={form.name || ''}
                    {...nameInputProps((v) => f('name', v))}
                    placeholder={field.display_label}
                    invalid={!!errors.name}
                  />
                </div>
              ) : field.field_name === 'title' ? (
                titleSelect
              ) : field.field_type === 'select' && SELECT_FIELD_OPTIONS[field.field_name] ? (
                <NativeSelect
                  value={form[field.field_name] || ''}
                  onChange={(e) => f(field.field_name, e.target.value)}
                  invalid={!!errors[field.field_name]}
                >
                  <option value="">Select</option>
                  {SELECT_FIELD_OPTIONS[field.field_name]!.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </NativeSelect>
              ) : (
                <Input
                  type={field.field_type}
                  value={form[field.field_name] || ''}
                  {...(field.field_name === 'name'
                    ? nameInputProps((v) => f('name', v))
                    : { onChange: (e: ChangeEvent<HTMLInputElement>) => f(field.field_name, e.target.value) })}
                  placeholder={field.display_label}
                  invalid={!!errors[field.field_name]}
                />
              )}
            </FormField>
          ))}
        </div>
      )}

    </Card>
    ) : (
    <Card>
      <div className="mb-1.5 text-xl font-bold">🖊 Consent</div>
      <div className="mb-5 text-[13px] text-ink-muted">
        Read each consent to {withTitle(form.name, form.title) || 'the patient'} and record their answer. Consent is asked once
        per patient — it carries forward to every future visit until it expires or is withdrawn.
      </div>
      {errors._ && <div className="mb-3 text-[13px] text-danger">{errors._}</div>}

      {consentLoading ? (
        <div className="text-[13px] text-ink-muted">Loading consent forms…</div>
      ) : consentLoadError ? (
        <div className="text-[13px] text-danger">
          Could not load the consent forms. Registration cannot be completed until they load — go back, check
          the connection and try again.
        </div>
      ) : (
        <ConsentCapturePanel
          items={consentItems}
          answers={consentAnswers}
          onAnswer={(type: ConsentTypeId, value: ConsentAnswerValue) =>
            setConsentAnswers((prev) => ({ ...prev, [type]: value }))}
          notes={consentNotes}
          onNotesChange={(type: ConsentTypeId, value: string) =>
            setConsentNotes((prev) => ({ ...prev, [type]: value }))}
          disabled={submitting || loading}
        />
      )}
    </Card>
    )}
    </StageLayout>
    {step === 'details' ? (
      <StageActionBar
        readiness={
          fieldsLoading ? 'Loading form…'
            : missingRequired > 0 ? `${missingRequired} required field${missingRequired === 1 ? '' : 's'} still empty`
            : consentStepNeeded ? '✓ Details complete — consent next'
            : '✓ Ready to register — no consent forms are enabled'
        }
        readinessTone={fieldsLoading ? 'neutral' : missingRequired > 0 ? 'warn' : 'success'}
      >
        {consentStepNeeded ? (
          <Button disabled={loading} onClick={handleContinueToConsent}>
            Continue to consent →
          </Button>
        ) : (
          <Button disabled={submitting || loading} loading={submitting} onClick={handleCompleteRegistration}>
            {submitting ? 'Completing…' : 'Complete registration →'}
          </Button>
        )}
      </StageActionBar>
    ) : (
      <StageActionBar
        readiness={
          consentLoading ? 'Loading consent forms…'
            : blockedByConsent.length > 0 ? `${blockedByConsent.map((i) => i.label).join(', ')} must be accepted to complete registration`
            : unanswered.length > 0 ? `${unanswered.length} consent${unanswered.length === 1 ? '' : 's'} still unanswered`
            : '✓ Consent recorded — ready to complete registration'
        }
        readinessTone={consentLoading ? 'neutral' : !consentComplete ? 'warn' : 'success'}
      >
        <Button variant="ghost" disabled={submitting || loading} onClick={() => setStep('details')}>
          ← Back to details
        </Button>
        <Button
          disabled={!consentComplete || submitting || loading || consentLoading}
          loading={submitting}
          onClick={handleCompleteRegistration}
        >
          {submitting ? 'Completing…' : 'Complete registration →'}
        </Button>
      </StageActionBar>
    )}
    </div>
  );
}

/** One node of the Registration sub-stepper above. */
function StepChip({ index, label, state }: { index: number; label: string; state: 'done' | 'current' | 'todo' }) {
  return (
    <span
      className={
        state === 'current'
          ? 'inline-flex items-center gap-1.5 rounded-full bg-teal px-2.5 py-1 font-semibold text-primary-foreground'
          : state === 'done'
            ? 'inline-flex items-center gap-1.5 rounded-full border border-success bg-success-soft px-2.5 py-1 font-semibold text-success-ink'
            : 'inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-ink-muted'
      }
    >
      <span className="font-mono text-[10.5px]">{state === 'done' ? '✓' : index}</span>
      {label}
    </span>
  );
}
